import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authApi } from "@/lib/apiClient";
import { AUTH } from "@/constants/testIds";

export default function PasswordResetConfirm() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await authApi.confirmReset(token, password);
      setDone(true);
      setTimeout(() => nav("/admin/login"), 1500);
    } catch (err) {
      setError(err.response?.data?.error?.message || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Card className="border shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">Choose a new password</CardTitle>
            <CardDescription>Use at least 8 characters.</CardDescription>
          </CardHeader>
          <CardContent>
            {done ? (
              <Alert>
                <AlertDescription>
                  Password updated. Redirecting to sign-in...
                </AlertDescription>
              </Alert>
            ) : (
              <form
                onSubmit={onSubmit}
                className="space-y-4"
                data-testid={AUTH.resetConfirmForm}
                noValidate
              >
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <div className="space-y-2">
                  <Label htmlFor="token">Reset token</Label>
                  <Input
                    id="token"
                    type="text"
                    required
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    data-testid={AUTH.resetConfirmToken}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid={AUTH.resetConfirmPassword}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={busy}
                  data-testid={AUTH.resetConfirmSubmit}
                >
                  {busy ? "Saving..." : "Set new password"}
                </Button>
                <Link
                  to="/admin/login"
                  className="text-sm text-muted-foreground hover:text-foreground block text-center"
                >
                  &larr; Back to sign in
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
